import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../../services/apiService';
import { DestinationAddress, toAddressBookEntry } from '../labelForm';

const AUTH_FAILED_MESSAGE =
  'Authentication failed: Please ensure you are logged in with valid Shipium credentials. You may need to log out and log back in to refresh your authentication token.';

export type SubmitStatus =
  | { phase: 'idle' }
  | { phase: 'submitting' }
  | { phase: 'error'; message: string };

const IDLE: SubmitStatus = { phase: 'idle' };

/** Saves a used destination to the address book unless it already exists. Never throws. */
async function saveAddressIfNew(address: DestinationAddress) {
  try {
    const entry = toAddressBookEntry(address);
    const { exists } = await apiService.checkAddressExists(entry);
    if (!exists) await apiService.createAddress(entry);
  } catch (error) {
    console.error('Failed to automatically save address:', error);
  }
}

/**
 * Creates the label job and goes straight to the dashboard. Leaving the page unmounts it,
 * so the form starts from defaults on the next visit.
 */
export function useLabelJobSubmit() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<SubmitStatus>(IDLE);

  const submit = useCallback(
    async (request: object, address: DestinationAddress) => {
      setStatus({ phase: 'submitting' });
      try {
        await apiService.createLabelJob(request);
        // Background save; it never throws and must not delay the redirect.
        void saveAddressIfNew(address);
        navigate('/dashboard');
      } catch (error: any) {
        // Nest validation errors arrive as string[].
        const raw: string | string[] | undefined = error?.response?.data?.message;
        const message = (Array.isArray(raw) ? raw.join('; ') : raw) || 'Failed to create label generation job';
        setStatus({ phase: 'error', message: message.includes('Authentication failed') ? AUTH_FAILED_MESSAGE : message });
      }
    },
    [navigate],
  );

  const dismissError = useCallback(() => setStatus((prev) => (prev.phase === 'error' ? IDLE : prev)), []);

  return { status, submit, dismissError };
}
